import React, { useEffect, useState } from 'react';
import TopBar from '../../../../Components/TopBar';
import Page from '../../../../Components/Containers/Page';
import NoItems from '../../../../Components/EmptyStates/NoItems';
import ConnectABank from './ConnectABank';
import { useConnectBank, useConnectedBanks, useConnectedBank } from '../../../../Data/Payments';
import { PageLoading } from '../../../../Components/Loading';
import Dropdown from '../../../../Components/Dropdown';
import { useNewWindow } from '../../../../Services/helpers';
import Button from '../../../../Components/Button/Button';
import DateTime from '../../../../Components/Formatting/DateTime';
import { useDialogues } from '../../../../Components/Dialogues';

const BankReconciliation = () => {
    const {banks} = useConnectBank();
    const {connectedBanks: connections, refresh} = useConnectedBanks();
    const [connecting, setConnecting] = useState(false);

    return <>
        <TopBar left="BackBtn" title="Bank Reconciliation" />
        <Page>
            <Inner {...{connections, banks, setConnecting, refresh}} />
        </Page>

        <ConnectABank isOpen={connecting} setIsOpen={setConnecting} onConnected={refresh} />
    </>
}

const Inner = ({connections, banks, setConnecting, refresh}) => {
    if (!connections || !banks) {
        return <PageLoading />
    }

    return <>
        <ul className="divide-y divide-gray-200">
            {connections.map(connection => <ConnectedBank
                    key={connection.id}
                    connection={connection}
                    bank={banks.find(b => b.id === connection?.connection?.institution_id)}
                    refreshBanks={refresh}
                />)}
        </ul>

        <NoItems
            icon="BuildingColumns"
            title="Connect a bank"
            message={<>
                Connect your bank accounts to automatically match bank transfers to your customers balances.
            </>}
            onClick={() => setConnecting(true)}
        />
    </>
}

const ConnectedBank = ({connection, bank, refreshBanks}) => {
    const {connectedBank: _connection, checkConnection, isSaving, deleteConnection} = useConnectedBank(connection.id);
    const theConnection = _connection ?? connection;
    const [refreshedOnce, setRefreshedOnce] = useState(false);
    const {confirm} = useDialogues();

    useEffect(() => {
        if (!_connection || refreshedOnce) {
            return;
        }
        setRefreshedOnce(true);
        checkConnection();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_connection, refreshedOnce]);

    return <li className="flex flex-row items-center justify-between gap-x-6 py-5 first:pt-0">
        <div className="shrink-0">
            <img src={bank.logo} alt={`Logo of ${bank.name}`} className="h-12 w-12 rounded" />
        </div>
        <div className="grow">
            <div className="flex flex-col items-start gap-x-3 text-sm">
                <p className="text-bold">
                    {bank.name}
                </p>
                <p className="text-muted">
                    {isSaving ? 'Checking connection...' : `Status: ${theConnection?.status}`}<br />

                    {theConnection?.expiresAt && <>
                        Expires <DateTime date={theConnection.expiresAt} hideTime />
                    </>}
                </p>
                <ConnectionAction connection={theConnection} {...{checkConnection, isSaving}} />
            </div>
        </div>
        <div className="shrink-0">
            <Dropdown loading={isSaving}
                options={[
                    {
                        icon: 'RotateRight',
                        title: 'Refresh',
                        onClick: () => checkConnection(),
                    },
                    {
                        icon: 'Trash',
                        title: 'Disconnect',
                        onClick: () => confirm(
                            {
                                theme: 'danger',
                                title: `Disconnect ${bank.name}?`,
                                message: `If you reconnect later you will need to manually match any bank transfers that have occurred since you disconnected, and you may experience duplicates.`,
                                confirmLabel: 'Disconnect'
                            },
                            async () => {
                                await deleteConnection();
                                refreshBanks();
                            }
                        )
                    }
                ]}
            />
        </div>
    </li>
}

const ConnectionAction = ({connection, checkConnection, isSaving}) => {
    const newWindow = useNewWindow();

    if (connection.status === 'Active' || connection.status === 'Deleted') {
        return null;
    }

    if (['Pending', 'Partial', 'Expiring'].includes(connection.status)) {
        return <Button size="md"
            theme="primary"
            label="Complete connection"
            icon="LinkExternal"
            onClick={() => newWindow(connection.connection.link)}
        />
    }

    if (['Expired', 'Broken'].includes(connection.status)) {
        return <Button size="md"
            theme="primary"
            label="Complete connection"
            icon="RotateRight"
            loading={isSaving}
            onClick={() => checkConnection()}
        />
    }

    return null;
}

export default BankReconciliation;
